import { inject, Injectable } from '@angular/core';
import { collection, collectionData, Firestore, Timestamp } from '@angular/fire/firestore';
import * as Sentry from '@sentry/angular';
import {
  catchError,
  combineLatest,
  concat,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  race,
  shareReplay,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Environment } from 'src/environments/evironment.interface';
import { RegionConfigService } from 'src/shared/services/region-config/region-config.service';

/** Matches `Environment['app']` so `environment.app` can be passed straight through. */
export type MaintenanceSite = Environment['app'];

/** Production regions a window can target, as written in the doc's `regions` field. */
export type MaintenanceRegion = 'UK' | 'US' | 'DE';

/** Accepted spellings in a doc's `regions` field (matched case-insensitively). */
const DOC_REGION_ALIASES: Record<string, MaintenanceRegion> = { UK: 'UK', GB: 'UK', US: 'US', DE: 'DE' };

/**
 * Maps an app region code (`RegionConfig.code`) to the production region whose windows apply to it.
 * Non-production codes (localhost, sandbox, testing, …) fall back to UK, except the US-specific ones.
 */
const REGION_CODE_TO_MAINTENANCE_REGION: Record<string, MaintenanceRegion> = {
  gb: 'UK',
  uk: 'UK',
  us: 'US',
  usstaging: 'US',
  sandboxus: 'US',
  seedingus: 'US',
  de: 'DE',
};

export function toMaintenanceRegion(regionCode: string): MaintenanceRegion {
  return REGION_CODE_TO_MAINTENANCE_REGION[regionCode] ?? 'UK';
}

export interface MaintenanceWindow {
  id: string;
  title: string;
  message: string;
  /** When the warning banner starts showing. Null means no warning is shown ahead of the block. */
  warningStartsAt: Date | null;
  /** When the site/app starts blocking access. */
  blockStartsAt: Date;
  /** Display-only: when we expect the block to end. The block only actually ends when `isActive` is flipped off. */
  expectedBlockEndsAt: Date | null;
  /** Master switch set by ops: nothing (warning or block) shows unless this is true. */
  isActive: boolean;
  /** Which sites this window affects. */
  sites: MaintenanceSite[];
  /** Which production regions this window affects. Never empty — docs without a region are dropped. */
  regions: MaintenanceRegion[];
}

interface MaintenanceWindowDoc {
  title: string;
  message: string;
  warningStartsAt?: Timestamp;
  blockStartsAt: Timestamp;
  expectedBlockEndsAt?: Timestamp;
  isActive: boolean;
  sites: MaintenanceSite[];
  regions: string[];
}

/** Firestore root collection this service reads from. */
export const MAINTENANCE_WINDOWS_COLLECTION = 'maintenance-windows';

/** setTimeout overflows past ~24.8 days, so boundaries further out than this are re-checked in steps. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * Maps a raw Firestore doc to a `MaintenanceWindow`, or `null` if it's missing `blockStartsAt`
 * (it can't be scheduled) or any recognised region (so a UK window can never block US, or vice
 * versa, just because someone forgot the field). Ops can and do hand-edit these docs in the Firestore
 * console, so this tolerates missing/malformed optional fields rather than crashing the whole list.
 */
export function toMaintenanceWindow(doc: MaintenanceWindowDoc & { id: string }): MaintenanceWindow | null {
  if (!isTimestamp(doc.blockStartsAt)) {
    console.error(`Maintenance window "${doc.id}" is missing blockStartsAt — skipping it.`);
    return null;
  }

  const regions = toDocRegions(doc.regions);
  if (!regions.length) {
    console.error(`Maintenance window "${doc.id}" has no recognised regions (expected UK, US or DE) — skipping it.`);
    return null;
  }

  return {
    id: doc.id,
    title: doc.title ?? '',
    message: doc.message ?? '',
    warningStartsAt: isTimestamp(doc.warningStartsAt) ? doc.warningStartsAt.toDate() : null,
    blockStartsAt: doc.blockStartsAt.toDate(),
    expectedBlockEndsAt: isTimestamp(doc.expectedBlockEndsAt) ? doc.expectedBlockEndsAt.toDate() : null,
    isActive: doc.isActive === true,
    sites: Array.isArray(doc.sites) ? doc.sites : [],
    regions,
  };
}

function toDocRegions(value: unknown): MaintenanceRegion[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const regions = value
    .map((region) => (typeof region === 'string' ? DOC_REGION_ALIASES[region.trim().toUpperCase()] : undefined))
    .filter((region): region is MaintenanceRegion => region !== undefined);
  return [...new Set(regions)];
}

function appliesTo(window: MaintenanceWindow, site: MaintenanceSite, region: MaintenanceRegion): boolean {
  return window.isActive && window.sites.includes(site) && window.regions.includes(region);
}

function isTimestamp(value: unknown): value is Timestamp {
  return !!value && typeof (value as Partial<Timestamp>).toDate === 'function';
}

/**
 * Windows affecting `site` in `region` in their warning period — active, `warningStartsAt` passed and
 * `blockStartsAt` not yet reached — soonest block first.
 */
export function selectUpcomingWindows(
  windows: MaintenanceWindow[],
  site: MaintenanceSite,
  region: MaintenanceRegion,
  now: number = Date.now()
): MaintenanceWindow[] {
  return windows
    .filter(
      (window) =>
        appliesTo(window, site, region) &&
        window.warningStartsAt !== null &&
        window.warningStartsAt.getTime() <= now &&
        window.blockStartsAt.getTime() > now
    )
    .sort((a, b) => a.blockStartsAt.getTime() - b.blockStartsAt.getTime());
}

/**
 * The window currently blocking `site` in `region`, or null if there isn't one. A window blocks from
 * `blockStartsAt` until ops flip `isActive` off — `expectedBlockEndsAt` is display-only.
 */
export function selectCurrentWindow(
  windows: MaintenanceWindow[],
  site: MaintenanceSite,
  region: MaintenanceRegion,
  now: number = Date.now()
): MaintenanceWindow | null {
  return windows.find((window) => appliesTo(window, site, region) && window.blockStartsAt.getTime() <= now) ?? null;
}

/** The next time after `now` at which a window moves into its warning or block period, or null if none. */
export function nextTransitionAt(windows: MaintenanceWindow[], now: number): number | null {
  const upcoming = windows
    .filter((window) => window.isActive)
    .flatMap((window) => [window.warningStartsAt?.getTime(), window.blockStartsAt.getTime()])
    .filter((time): time is number => time !== undefined && time > now);
  return upcoming.length ? Math.min(...upcoming) : null;
}

/**
 * Backstop for the boundary timer below: timers are paused while the device sleeps and throttled or
 * frozen in background tabs, so a boundary can pass long before its timer fires.
 */
const RECHECK_INTERVAL_MS = 30_000;
const recheck$: Observable<unknown> = merge(
  interval(RECHECK_INTERVAL_MS),
  typeof document === 'undefined'
    ? EMPTY
    : fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
);

/**
 * Emits the current time now, and again each time one of `windows` crosses a boundary.
 * Warnings and blocks start on a clock rather than on a Firestore write, so without this a
 * user already on a page would never see them kick in.
 */
export function clockAtTransitions(windows: MaintenanceWindow[]): Observable<number> {
  return defer(() => {
    const now = Date.now();
    const next = nextTransitionAt(windows, now);
    if (next === null) {
      return of(now);
    }
    // Re-evaluate at the boundary or on the next recheck, whichever comes first. Either way this
    // re-arms, so a timer that fires fractionally early just finds the same boundary again.
    return concat(
      of(now),
      race(timer(Math.min(next - now, MAX_TIMER_DELAY_MS)), recheck$).pipe(
        take(1),
        switchMap(() => clockAtTransitions(windows))
      )
    );
  });
}

@Injectable({
  providedIn: 'root',
})
export class MaintenanceWindowService {
  private readonly firestore = inject(Firestore);
  private readonly region$ = inject(RegionConfigService).region$.pipe(map(toMaintenanceRegion));

  // `defer` builds the query lazily on first subscription, rather than at service construction,
  // so a synchronous Firestore error surfaces through `catchError` below instead of escaping it.
  private readonly windows$: Observable<MaintenanceWindow[]> = defer(() => {
    if (environment.environment === 'staging' || environment.environment === 'usstaging') {
      let empty: (MaintenanceWindowDoc & { id: string })[] = [];
      return of(empty);
    }
    return collectionData(collection(this.firestore, MAINTENANCE_WINDOWS_COLLECTION), { idField: 'id' }) as Observable<
      (MaintenanceWindowDoc & { id: string })[]
    >;
  }).pipe(
    map((docs) => docs.map(toMaintenanceWindow).filter((window): window is MaintenanceWindow => window !== null)),
    catchError((error: unknown) => {
      // A failed fetch resolves to [], which would silently hide an active window from users —
      // report it to Sentry directly, since console.error alone never reaches Sentry.
      console.error('Error fetching maintenance windows:', error);
      Sentry.captureException(error instanceof Error ? error : new Error('Failed to fetch maintenance windows'), {
        tags: { maintenance_window_fetch: 'error' },
      });
      return of([] as MaintenanceWindow[]);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Region is part of the input as well as the Firestore snapshot: the region switcher can change
  // it at runtime, and a UK window must stop applying the moment someone switches to US.
  getUpcomingWindows(site: MaintenanceSite): Observable<MaintenanceWindow[]> {
    return combineLatest([this.windows$, this.region$]).pipe(
      switchMap(([windows, region]) =>
        clockAtTransitions(windows).pipe(
          map((now) => selectUpcomingWindows(windows, site, region, now)),
          // Rechecks re-emit on a timer; only pass on an actual change within this snapshot.
          distinctUntilChanged((a, b) => a.length === b.length && a.every((window, i) => window === b[i]))
        )
      )
    );
  }

  getCurrentWindow(site: MaintenanceSite): Observable<MaintenanceWindow | null> {
    return combineLatest([this.windows$, this.region$]).pipe(
      switchMap(([windows, region]) =>
        clockAtTransitions(windows).pipe(map((now) => selectCurrentWindow(windows, site, region, now)))
      ),
      distinctUntilChanged()
    );
  }
}
